/**
 * Base URL du site client (dog-reservation) pour liens publics et widget.
 *
 * - Priorité aux variables explicites (tous environnements).
 * - En `development` ou `test` sans variable : front client local (évite la prod pendant les tests).
 * - En `production` sans variable : domaine public par défaut.
 */
export function getPublicClientBookingOrigin(): string {
  const fromEnv =
    process.env.REACT_APP_CLIENT_BOOKING_URL?.trim() ||
    process.env.REACT_APP_FRONTEND_URL?.trim();
  if (fromEnv) {
    return fromEnv.replace(/\/$/, '');
  }

  const nodeEnv = process.env.NODE_ENV;
  if (nodeEnv === 'development' || nodeEnv === 'test') {
    // Le back-office prend souvent 3000 en local, donc le front réservation démarre sur 3001.
    const port = process.env.REACT_APP_CLIENT_BOOKING_DEV_PORT?.trim() || '3001';
    return `http://localhost:${port}`;
  }

  return 'https://www.rintintin-booking.com';
}

/**
 * Origine pour les liens **partageables** d’événement (email / copier / ouvrir).
 *
 * Ne jamais utiliser `REACT_APP_FRONTEND_URL` : en build Pro elle vaut
 * `rintintin-pro.com` et produit des 404 (ticket #768).
 */
export function getPublicEventShareOrigin(): string {
  const fromEnv =
    process.env.REACT_APP_CLIENT_BOOKING_URL?.trim() ||
    process.env.REACT_APP_BOOKING_PUBLIC_URL?.trim() ||
    process.env.REACT_APP_FRONT_URL?.trim();
  if (fromEnv) {
    return fromEnv.replace(/\/$/, '');
  }

  const nodeEnv = process.env.NODE_ENV;
  if (nodeEnv === 'development' || nodeEnv === 'test') {
    const port = process.env.REACT_APP_CLIENT_BOOKING_DEV_PORT?.trim() || '3001';
    return `http://localhost:${port}`;
  }

  return 'https://www.rintintin-booking.com';
}

/** URL publique Booking d’un événement (slug). */
export function publicEventUrl(slug: string): string {
  const safe = String(slug || '').trim();
  return `${getPublicEventShareOrigin()}/event/${safe}`;
}

/** True si l’URL est collable sur les réseaux (pas localhost / 127.0.0.1). */
export function isPublicShareableBookingUrl(url: string): boolean {
  const raw = (url || '').trim();
  if (!raw) return false;
  try {
    const host = new URL(raw).hostname.toLowerCase();
    return host !== 'localhost' && host !== '127.0.0.1';
  } catch {
    return false;
  }
}
